import { useUser } from "@/hooks/useUser";
import { motion } from "framer-motion";
import { Sparkles, Zap } from "lucide-react";
import { useState } from "react";
import { useAuthRequiredModal } from "./auth/auth-required-modal";
import { SubscriptionPlansModal } from "./subscription-plans-modal";
import { Button } from "./ui/button";

interface PaywallProps {
  title?: string;
  description?: string;
  features?: string[];
}

export function Paywall({
  title = "Meet Your Personal Real Estate AI",
  description = "Unlike general AI, KeyMate understands your exact home buying progress and specializes in each specific step of your journey",
  features = [
    "Step-specific guidance tailored to where you are in your purchase",
    "Context-aware AI that knows your property details and preferences",
    "Specialized knowledge of local real estate processes and terms",
    "Instant document analysis with real estate expertise",
  ],
}: PaywallProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useUser();
  const { requireAuth, AuthRequiredModal } = useAuthRequiredModal();

  const handleUpgradeClick = async () => {
    if (!user) {
      try {
        await requireAuth();
        // After successful auth, show subscription modal
        setIsModalOpen(true);
      } catch {
        // User dismissed auth modal
        return;
      }
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <div className="flex h-full w-full flex-col items-center justify-center p-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-canary">
            <Sparkles className="h-6 w-6 text-smokeyBlack" />
          </div>

          <h3 className="mb-2 text-xl font-bold text-smokeyBlack">{title}</h3>
          <p className="mb-6 text-sm text-darkGray">{description}</p>

          <div className="mb-8 space-y-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-2 text-sm text-left"
              >
                <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-canary/20">
                  <Sparkles className="h-3 w-3 text-smokeyBlack" />
                </div>
                <span className="text-darkGray">{feature}</span>
              </motion.div>
            ))}
          </div>

          <Button
            onClick={handleUpgradeClick}
            className="w-full bg-canary text-smokeyBlack hover:bg-canary/90"
          >
            <Zap className="mr-2 h-4 w-4" />
            Unlock Premium Features
          </Button>

          <p className="mt-4 text-xs text-darkGray">
            Fine tuned with real estate transactions and local market knowledge
          </p>
        </motion.div>
      </div>

      <AuthRequiredModal
        title="Sign in to Upgrade"
        description="Get instant access to your personal AI real estate assistant and unlock all premium features."
        signInButtonText="Continue with Google"
      />

      <SubscriptionPlansModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
